import React, { useMemo, useState } from "react";
import { Search, Plus, X, ChevronDown } from "lucide-react";
import {
    INTEREST_CATEGORIES, INTEREST_VIBES, DEFAULT_VIBE, interestLabel,
} from "../lib/interests";

/**
 * Drill-down interest picker with a 3-state vibe per pick + free-text custom
 * interests + search. Controlled — emits the full next shape on every change:
 *   onChange({ interests, ranked_interests, custom_interests })
 */
export const InterestPicker = ({ interests = [], ranked = {}, custom = [], onChange }) => {
    const [expanded, setExpanded] = useState(null);
    const [query, setQuery] = useState("");
    const [draft, setDraft] = useState("");

    const emit = (next) =>
        onChange({ interests, ranked_interests: ranked, custom_interests: custom, ...next });

    const toggleSub = (slug) => {
        if (interests.includes(slug)) {
            const { [slug]: _drop, ...rest } = ranked; // eslint-disable-line no-unused-vars
            emit({ interests: interests.filter((s) => s !== slug), ranked_interests: rest });
        } else {
            emit({
                interests: [...interests, slug],
                ranked_interests: { ...ranked, [slug]: DEFAULT_VIBE },
            });
        }
    };
    const setVibe = (slug, vibe) => emit({ ranked_interests: { ...ranked, [slug]: vibe } });

    const addCustom = () => {
        const t = draft.trim();
        if (!t) return;
        if (!custom.some((c) => c.toLowerCase() === t.toLowerCase())) {
            emit({ custom_interests: [...custom, t] });
        }
        setDraft("");
    };
    const removeCustom = (t) => emit({ custom_interests: custom.filter((c) => c !== t) });

    const searchResults = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return null;
        const seen = new Set();
        const out = [];
        INTEREST_CATEGORIES.forEach((cat) =>
            (cat.subs || []).forEach((s) => {
                if (seen.has(s.slug)) return;
                if (s.label.toLowerCase().includes(q) || s.slug.includes(q)) {
                    seen.add(s.slug);
                    out.push(s);
                }
            })
        );
        return out;
    }, [query]);

    const countFor = (cat) =>
        (cat.subs || []).filter((s) => interests.includes(s.slug)).length;

    const expandedCat = INTEREST_CATEGORIES.find((c) => c.key === expanded);

    return (
        <div data-testid="interest-picker" className="space-y-5">
            {/* Search */}
            <div className="relative">
                <Search className="w-4 h-4 text-slate-gray absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                    data-testid="interest-search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search outdoor interests — rappelling, shed hunting, disc golf…"
                    className="w-full pl-9 pr-4 py-3 rounded-md bg-snowcap border border-ridge-line focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/20 text-granite text-sm"
                />
            </div>

            {searchResults ? (
                <div className="flex flex-wrap gap-2" data-testid="interest-search-results">
                    {searchResults.length === 0 ? (
                        <p className="text-sm text-slate-gray">
                            No match. Add “{query.trim()}” as a custom interest below.
                        </p>
                    ) : (
                        searchResults.map((s) => (
                            <Chip
                                key={s.slug}
                                slug={s.slug}
                                label={s.label}
                                active={interests.includes(s.slug)}
                                onClick={() => toggleSub(s.slug)}
                            />
                        ))
                    )}
                </div>
            ) : (
                <>
                    {/* Category cards */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {INTEREST_CATEGORIES.map((cat) => {
                            const Icon = cat.icon;
                            const n = cat.isCustom ? custom.length : countFor(cat);
                            const isOpen = expanded === cat.key;
                            return (
                                <button
                                    key={cat.key}
                                    type="button"
                                    data-testid={`interest-cat-${cat.key}`}
                                    onClick={() => setExpanded(isOpen ? null : cat.key)}
                                    className={`relative flex items-center gap-2 p-3 rounded-lg border text-left transition-all ${
                                        isOpen
                                            ? "border-ember-orange bg-ember-orange/5"
                                            : "border-ridge-line bg-snowcap hover:border-midnight-pine"
                                    }`}
                                >
                                    <span className="w-8 h-8 rounded-md bg-trail-tan text-midnight-pine flex items-center justify-center shrink-0">
                                        <Icon className="w-4 h-4" />
                                    </span>
                                    <span className="flex-1 min-w-0">
                                        <span className="block text-sm font-bold text-midnight-pine leading-tight">
                                            {cat.label}
                                        </span>
                                    </span>
                                    {n > 0 && (
                                        <span
                                            data-testid={`interest-cat-count-${cat.key}`}
                                            className="shrink-0 w-5 h-5 rounded-full bg-ember-orange text-white text-[10px] font-bold flex items-center justify-center"
                                        >
                                            {n}
                                        </span>
                                    )}
                                    <ChevronDown
                                        className={`w-4 h-4 text-slate-gray transition-transform ${isOpen ? "rotate-180" : ""}`}
                                    />
                                </button>
                            );
                        })}
                    </div>

                    {/* Expanded panel */}
                    {expandedCat && (
                        <div
                            data-testid={`interest-panel-${expandedCat.key}`}
                            className="rounded-lg border border-ridge-line bg-alpine-mist/40 p-4"
                        >
                            {expandedCat.helper && (
                                <p className="text-xs text-slate-gray mb-3">{expandedCat.helper}</p>
                            )}
                            {expandedCat.isCustom ? (
                                <div>
                                    <div className="flex gap-2">
                                        <input
                                            data-testid="interest-custom-input"
                                            value={draft}
                                            onChange={(e) => setDraft(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") { e.preventDefault(); addCustom(); }
                                            }}
                                            placeholder="e.g. magnet fishing"
                                            className="flex-1 px-4 py-2.5 rounded-md bg-snowcap border border-ridge-line focus:border-ember-orange focus:outline-none text-granite text-sm"
                                        />
                                        <button
                                            type="button"
                                            data-testid="interest-custom-add"
                                            onClick={addCustom}
                                            className="px-4 rounded-md bg-midnight-pine text-white font-bold text-sm hover:bg-deep-evergreen transition-all"
                                        >
                                            <Plus className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-wrap gap-2">
                                    {expandedCat.subs.map((s) => (
                                        <Chip
                                            key={s.slug}
                                            slug={s.slug}
                                            label={s.label}
                                            active={interests.includes(s.slug)}
                                            onClick={() => toggleSub(s.slug)}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {/* Your picks + 3-state vibe */}
            {(interests.length > 0 || custom.length > 0) && (
                <div data-testid="interest-picks">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-slate-gray mb-3">
                        Your interests — how into each are you?
                    </h3>
                    <div className="space-y-2">
                        {interests.map((slug) => (
                            <div
                                key={slug}
                                data-testid={`interest-pick-${slug}`}
                                className="flex items-center gap-2 p-2.5 rounded-md bg-snowcap border border-ridge-line"
                            >
                                <span className="text-sm font-bold text-midnight-pine flex-1 min-w-0 truncate">
                                    {interestLabel(slug)}
                                </span>
                                <div className="flex gap-1 shrink-0">
                                    {INTEREST_VIBES.map((v) => (
                                        <button
                                            key={v.id}
                                            type="button"
                                            data-testid={`interest-vibe-${slug}-${v.id}`}
                                            onClick={() => setVibe(slug, v.id)}
                                            className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-all ${
                                                (ranked[slug] || DEFAULT_VIBE) === v.id
                                                    ? "bg-ember-orange text-white"
                                                    : "bg-alpine-mist text-granite hover:bg-trail-tan"
                                            }`}
                                        >
                                            {v.label}
                                        </button>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    data-testid={`interest-remove-${slug}`}
                                    onClick={() => toggleSub(slug)}
                                    className="w-6 h-6 rounded-full hover:bg-trail-red/10 text-slate-gray hover:text-trail-red flex items-center justify-center shrink-0"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        ))}
                        {custom.map((t) => (
                            <div
                                key={t}
                                data-testid={`interest-custom-${t}`}
                                className="flex items-center gap-2 p-2.5 rounded-md bg-snowcap border border-dashed border-ridge-line"
                            >
                                <span className="text-sm font-bold text-midnight-pine flex-1 min-w-0 truncate">{t}</span>
                                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-gray">Custom</span>
                                <button
                                    type="button"
                                    data-testid={`interest-custom-remove-${t}`}
                                    onClick={() => removeCustom(t)}
                                    className="w-6 h-6 rounded-full hover:bg-trail-red/10 text-slate-gray hover:text-trail-red flex items-center justify-center shrink-0"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const Chip = ({ slug, label, active, onClick }) => (
    <button
        type="button"
        data-testid={`interest-chip-${slug}`}
        onClick={onClick}
        className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
            active
                ? "bg-ember-orange text-white"
                : "bg-snowcap text-granite border border-ridge-line hover:border-ember-orange"
        }`}
    >
        {label}
    </button>
);

export default InterestPicker;
